// =====================================================
// USE PRICING RULE FORM HOOK
// Stage 2: Participants & Pricing
// Hook for managing pricing rule form state
// =====================================================

import { useState, useMemo, useCallback } from 'react';
import type {
  PricingRule,
  PricingActionType,
  CreatePricingRuleData,
  UpdatePricingRuleData,
} from '@/types/pricing.types';
import { validatePricingRuleForm, type ConditionLookups } from '@/models/pricingRule';
import {
  draftFromCondition,
  draftProblem,
  draftsToConditions,
  newDraft,
  suggestRuleName,
  type ConditionDraft,
  type DraftKind,
  type RuleTemplate,
} from '../utils/conditionDrafts';

// ===================
// TYPES
// ===================

export interface PricingRuleFormData {
  name: string;
  description: string;
  is_active: boolean;
  conditions: ConditionDraft[];
  action_type: PricingActionType | null;
  action_value: number | null;
}

export interface PricingRuleFormErrors {
  name?: string;
  description?: string;
  actionType?: string;
  actionValue?: string;
  conditions?: string;
}

// ===================
// INITIAL STATE
// ===================

const getInitialFormData = (existingRule?: PricingRule | null): PricingRuleFormData => {
  if (existingRule) {
    return {
      name: existingRule.name,
      description: existingRule.description || '',
      is_active: existingRule.is_active,
      conditions: existingRule.conditions.map(draftFromCondition),
      action_type: existingRule.action_type,
      action_value: existingRule.action_value,
    };
  }

  return {
    name: '',
    description: '',
    is_active: true,
    conditions: [],
    action_type: null,
    action_value: null,
  };
};

const conditionsJson = (drafts: ConditionDraft[]) => JSON.stringify(draftsToConditions(drafts));

// ===================
// HOOK
// ===================

export function usePricingRuleForm(existingRule?: PricingRule | null) {
  const [formData, setFormData] = useState<PricingRuleFormData>(() => getInitialFormData(existingRule));
  const [errors, setErrors] = useState<PricingRuleFormErrors>({});
  // Per-row problems stay hidden until the first submit attempt
  const [showProblems, setShowProblems] = useState(false);

  // Original values for change detection (follows the rule being edited)
  const original = useMemo(() => getInitialFormData(existingRule), [existingRule]);

  const hasChanges = useMemo(() => {
    if (!existingRule) return true; // New rule, always has "changes"

    return (
      formData.name.trim() !== original.name ||
      formData.description.trim() !== original.description ||
      formData.is_active !== original.is_active ||
      formData.action_type !== original.action_type ||
      formData.action_value !== original.action_value ||
      conditionsJson(formData.conditions) !== conditionsJson(original.conditions)
    );
  }, [formData, original, existingRule]);

  const updateField = useCallback(
    <K extends keyof PricingRuleFormData>(field: K, value: PricingRuleFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field === 'action_type' ? 'actionType' : field === 'action_value' ? 'actionValue' : field]: undefined }));
    },
    []
  );

  const addCondition = useCallback((kind?: DraftKind) => {
    setFormData((prev) => ({ ...prev, conditions: [...prev.conditions, newDraft(kind)] }));
  }, []);

  const updateCondition = useCallback((key: string, patch: Partial<ConditionDraft>) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    }));
    setErrors((prev) => ({ ...prev, conditions: undefined }));
  }, []);

  const removeCondition = useCallback((key: string) => {
    setFormData((prev) => ({ ...prev, conditions: prev.conditions.filter((c) => c.key !== key) }));
    setErrors((prev) => ({ ...prev, conditions: undefined }));
  }, []);

  const applyTemplate = useCallback((template: RuleTemplate) => {
    setFormData((prev) => ({
      ...prev,
      conditions: template.conditions(),
      action_type: template.action_type,
      action_value: template.action_value,
    }));
    setErrors({});
    setShowProblems(false);
  }, []);

  /** Name used when the field is left blank */
  const getSuggestedName = useCallback(
    (lookups?: ConditionLookups) =>
      suggestRuleName(draftsToConditions(formData.conditions), formData.action_type, formData.action_value, lookups),
    [formData.conditions, formData.action_type, formData.action_value]
  );

  // Validate form (incomplete criteria block saving instead of being dropped silently)
  const validate = useCallback(
    (basePrice?: number): boolean => {
      const validation = validatePricingRuleForm({
        name: 'x', // blank names fall back to the suggested one
        actionType: formData.action_type,
        actionValue: formData.action_value,
        basePrice,
      });
      const next = validation.errors as PricingRuleFormErrors;

      if (formData.conditions.some((c) => draftProblem(c) !== null)) {
        next.conditions = 'Completa o quita los criterios marcados antes de guardar.';
      }

      setShowProblems(true);
      setErrors(next);
      return Object.keys(next).length === 0;
    },
    [formData]
  );

  const getCreateData = useCallback(
    (eventId: string, lookups?: ConditionLookups): CreatePricingRuleData => ({
      event_id: eventId,
      name: formData.name.trim() || getSuggestedName(lookups),
      description: formData.description.trim() || undefined,
      is_active: formData.is_active,
      conditions: draftsToConditions(formData.conditions),
      action_type: formData.action_type!,
      action_value: formData.action_value!,
    }),
    [formData, getSuggestedName]
  );

  const getUpdateData = useCallback(
    (lookups?: ConditionLookups): UpdatePricingRuleData => {
      const updates: UpdatePricingRuleData = {};

      if (formData.name.trim() !== original.name) {
        updates.name = formData.name.trim() || getSuggestedName(lookups);
      }
      if (formData.description.trim() !== original.description) {
        updates.description = formData.description.trim(); // '' clears it
      }
      if (formData.is_active !== original.is_active) {
        updates.is_active = formData.is_active;
      }
      if (formData.action_type !== original.action_type) {
        updates.action_type = formData.action_type!;
      }
      if (formData.action_value !== original.action_value) {
        updates.action_value = formData.action_value!;
      }
      if (conditionsJson(formData.conditions) !== conditionsJson(original.conditions)) {
        updates.conditions = draftsToConditions(formData.conditions);
      }

      return updates;
    },
    [formData, original, getSuggestedName]
  );

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData(existingRule));
    setErrors({});
    setShowProblems(false);
  }, [existingRule]);

  return {
    formData,
    errors,
    showProblems,
    hasChanges,
    updateField,
    addCondition,
    updateCondition,
    removeCondition,
    applyTemplate,
    getSuggestedName,
    validate,
    getCreateData,
    getUpdateData,
    resetForm,
  };
}
